
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MapPin, Users } from 'lucide-react';

interface FavoriteHostsProps {
  favorites: any[];
}

export const FavoriteHosts = ({ favorites }: FavoriteHostsProps) => {
  if (!favorites || !Array.isArray(favorites)) {
    console.warn('FavoriteHosts component received invalid favorites data:', favorites);
    favorites = [];
  }

  // Filter to ensure we only show hosts with host data
  const hostFavorites = favorites.filter(f => 
    f.type === 'host' && f.host && f.host.id
  );

  if (hostFavorites.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center">
        <h3 className="text-lg font-medium mb-2">No favorite hosts yet</h3>
        <p className="text-gray-500">
          Save hosts by clicking the heart icon when browsing host profiles.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {hostFavorites.map((favorite) => (
        <Link to={`/host/${favorite.host.id}`} key={favorite.id}>
          <Card className="h-full hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center gap-4">
              <Avatar className="w-12 h-12">
                <AvatarImage src={favorite.host.avatar_url || undefined} />
                <AvatarFallback>
                  {favorite.host.username?.[0]?.toUpperCase() || '?'}
                </AvatarFallback>
              </Avatar>
              <CardTitle>
                <div>
                  <span className="text-xl">{favorite.host.username}</span>
                  {favorite.host.location && (
                    <div className="flex items-center gap-2 text-gray-600 text-sm mt-1">
                      <MapPin className="w-4 h-4" />
                      <span>{favorite.host.location}</span>
                    </div>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {favorite.host.bio && (
                <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                  {favorite.host.bio}
                </p>
              )}
              {favorite.host.languages && favorite.host.languages.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Users className="w-4 h-4" />
                  <span>Speaks: {favorite.host.languages.join(', ')}</span>
                </div>
              )}
              {favorite.notes && (
                <p className="mt-4 text-sm border-t pt-4 text-gray-600">
                  Note: {favorite.notes}
                </p>
              )}
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
};
